import { Purchases, LOG_LEVEL, PurchasesPackage, CustomerInfo, PurchasesOfferings } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';
import { captureError, captureWarning, recordBreadcrumb } from '@/lib/sentry';
import { isUserCancelledPurchase } from '@/lib/billingPlatform';
// Bu dördü `import.meta.env` okuyor; ayrı modülde durmasalardı bu dosya
// test ortamında import EDİLEMEZDİ. Bkz. lib/billingConfig.ts
import {
  RC_IOS_KEY,
  RC_ANDROID_KEY,
  IS_DEV,
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
} from '@/lib/billingConfig';

export const initRevenueCat = async (appUserId: string) => {
  if (!Capacitor.isNativePlatform()) return;

  try {
    if (IS_DEV) {
      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
    } else {
      await Purchases.setLogLevel({ level: LOG_LEVEL.INFO });
    }

    if (Capacitor.getPlatform() === 'ios') {
      if (!RC_IOS_KEY) {
        captureWarning("RevenueCat iOS Key eksik", { service: 'revenuecat', platform: 'ios' });
      }
      await Purchases.configure({ apiKey: RC_IOS_KEY, appUserID: appUserId });
    } else if (Capacitor.getPlatform() === 'android') {
      if (!RC_ANDROID_KEY) {
        captureWarning("RevenueCat Android Key eksik", { service: 'revenuecat', platform: 'android' });
      }
      await Purchases.configure({ apiKey: RC_ANDROID_KEY, appUserID: appUserId });
    }
  } catch (error) {
    captureError(error, { service: 'revenuecat', action: 'initRevenueCat' });
  }
};

export const getOfferings = async (): Promise<PurchasesOfferings | null> => {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const offerings = await Purchases.getOfferings();
    return offerings;
  } catch (error) {
    captureError(error, { service: 'revenuecat', action: 'getOfferings' });
    return null;
  }
};

export const purchasePackage = async (packageToBuy: PurchasesPackage): Promise<CustomerInfo | null> => {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: packageToBuy });
    return customerInfo;
  } catch (error: any) {
    // RevenueCat iptali `code: '1'` + `userCancelled: true` ile bildirir (GUNUM-VAR-1S).
    if (isUserCancelledPurchase(error)) {
      recordBreadcrumb('Kullanıcı satın almayı iptal etti', {
        service: 'revenuecat',
        package: packageToBuy.identifier,
      });
      return null;
    }
    captureError(error, { service: 'revenuecat', action: 'purchasePackage', package: packageToBuy.identifier });
    throw error;
  }
};

export const restorePurchases = async (): Promise<CustomerInfo | null> => {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const { customerInfo } = await Purchases.restorePurchases();
    return customerInfo;
  } catch (error) {
    captureError(error, { service: 'revenuecat', action: 'restorePurchases' });
    throw error;
  }
};

export const getCustomerInfo = async (): Promise<CustomerInfo | null> => {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return customerInfo;
  } catch (error) {
    captureError(error, { service: 'revenuecat', action: 'getCustomerInfo' });
    return null;
  }
};

/**
 * RevenueCat (native iOS/Android) mevcut mu kontrol et
 */
export function isRevenueCatAvailable(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Platform tespiti: iOS mu Android mu
 */
export function getNativePlatform(): 'ios' | 'android' | 'web' {
  if (!Capacitor.isNativePlatform()) return 'web';
  return Capacitor.getPlatform() as 'ios' | 'android';
}

/**
 * Satın alma sonrası backend'e doğrulama gönder → pro_tier güncelle
 */
export async function verifyAndUpdateTier(
  customerInfo: CustomerInfo,
  userId: string,
  purchasedTier?: 'annual' | 'lifetime'
): Promise<{ success: boolean; tier?: string; error?: string }> {
  const supabaseUrl = SUPABASE_URL;
  const supabaseAnonKey = SUPABASE_ANON_KEY;

  let tier: 'annual' | 'lifetime' | 'free' = purchasedTier || 'free';

  if (!purchasedTier) {
    const activeEntitlements = customerInfo.entitlements?.active || {};
    
    const proAccess = activeEntitlements['pro_access'] 
      || activeEntitlements['lifetime'] 
      || activeEntitlements['pro_lifetime'];
    
    if (proAccess) {
      const expirationDate = (proAccess as any).expirationDate;
      const productId = ((proAccess as any).productIdentifier || '').toLowerCase();
      
      // Sandbox'ta hem lifetime hem annual alınmışsa, lifetime her zaman aktif kalır.
      // Entitlement eğer süreliyse (expirationDate varsa), kesinlikle annual (abonelik)tir.
      // Ama productId 'annual', 'yillik' de içerebilir.
      if (productId.includes('lifetime') || productId.includes('forever')) {
        tier = 'lifetime';
      } else if (productId.includes('annual') || productId.includes('year') || productId.includes('yillik')) {
        tier = 'annual';
      } else if (expirationDate) {
        tier = 'annual';
      } else {
        tier = 'lifetime'; // Süresiz
      }
    } else if ('annual' in activeEntitlements || 'pro' in activeEntitlements || 'pro_annual' in activeEntitlements) {
      tier = 'annual';
    }
  }

  if (tier === 'free') {
    return { success: false, error: 'Aktif entitlement bulunamadı' };
  }

  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/play-billing-webhook`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        type: 'REVENUECAT_VERIFICATION',
        userId,
        tier,
        platform: getNativePlatform(),
        rcUserId: customerInfo.originalAppUserId,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || 'Doğrulama başarısız' };
    }

    return { success: true, tier: data.tier || tier };
  } catch (error: any) {
    captureError(error, { service: 'revenuecat', action: 'verifyAndUpdateTier', userId });
    return { success: false, error: error.message };
  }
}

